import { until as waitUntil } from '@vueuse/core'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { NATIVE_APP_CLIDS } from '@/constants/general'
import type { AdType } from '@/modules/adv/types'
import type { ChargeResponse, DisableAdOrder } from '@/modules/in-game-payments'
import { useNativeExactPackPurchase } from '@/modules/native-payments/native-exact-pack-purchase'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import { CLIENT_QUERY } from '@/router/query-params'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { usePromoCodeStore } from '@/store/promo-code/promo-code-store'
import { useUserStore } from '@/store/user-store/user-store'
import { getConfigByClid } from '@/utils/clid-config'

export const useAdDisabledStore = defineStore('ad-disabled', () => {
    const appStore = useAppStore()
    const clientAppStore = useClientAppStore()
    const adsStore = useAdsStore()
    const generalData = useGeneralData()
    const userStore = useUserStore()
    const layoutStore = useLayoutStore()
    const loggerStore = useLoggerStore()
    const { chargeUser: chargeUserWithNativeExactPack } = useNativeExactPackPurchase(layoutStore.setPaymentModalOrder)
    const pricesConfig = computed(() => generalData.mainData.value.noAdsConfigInGam)
    const { user } = storeToRefs(userStore)
    const { isPortalEnv, initialQuery, clid, isExportOnWidget } = storeToRefs(appStore)
    const clidConfig = computed(() => getConfigByClid(clid.value))
    const route = useRoute()
    const isOutOfPlaygama = computed(() => !isPortalEnv.value && !isExportOnWidget.value)
    const isExportPage = computed(() => route.name === ROUTE_NAMES.EXPORT_GAME_PAGE)

    const isAdDisabled = computed(() => {
        // special queries for export page
        if (isExportPage.value && !!initialQuery.value?.[CLIENT_QUERY.USE_NO_ADS]) {
            return true
        }
        // Native TWA clid
        if (clid.value === NATIVE_APP_CLIDS.twa) {
            return true
        }

        // Partner clids with isAdDisabled flag (TWA partners, Xiaomi test, etc.)
        if (clidConfig.value.isAdDisabled && isOutOfPlaygama.value && isExportPage.value) {
            return true
        }
        return isAdFreeActive.value
    })

    const isAdFreeActive = ref(false)
    const remainingMsWithoutAd = ref(0)

    function checkAdDisabled() {
        const until = user.value?.adsDisabledUntil
        const timeRemainingMs = isPortalEnv.value && until ? new Date(until).getTime() - Date.now() : 0
        isAdFreeActive.value = timeRemainingMs > 0
        adsStore.updateTargeting({
            pg_ad_disabled: isAdDisabled.value.toString(),
        })
        remainingMsWithoutAd.value = isAdFreeActive.value ? timeRemainingMs : 0
    }

    if (import.meta.env.SSR === false) {
        checkAdDisabled()
        setInterval(checkAdDisabled, 60_000)
        watch(() => user.value?.adsDisabledUntil, checkAdDisabled)
        watch(() => route.path, closeNoAdFinalScreen)
        watch(
            () => isAdDisabled.value,
            () => adsStore.adService?.shutdown(),
        )
    }

    const adDisabledModalOptions = ref({
        isOpened: false,
        isTopUp: false,
        placement: undefined as AdType | undefined,
        trigger: '',
    })
    const isAdFinalScreenVisible = ref(false)
    const resolveModalFn = ref<((value: ChargeResponse<DisableAdOrder> | null) => void) | null>(null)

    // if the user closed the modal without purchasing, we still check active subscription
    function getAdFreeResult() {
        return isAdFreeActive.value
            ? {
                  data: { status: 'PAID' as const, orderId: '', amount: 0 },
                  errors: [],
                  originalResponse: null as unknown as Response,
              }
            : null
    }

    function closeAdDisabledModal() {
        resolveModalFn.value?.(getAdFreeResult())
        resolveModalFn.value = null
        adDisabledModalOptions.value = {
            isOpened: false,
            isTopUp: false,
            placement: undefined,
            trigger: '',
        }
    }

    // settles this purchase session once, after the code sent from the promo popup is done (even if the
    // popup was closed mid-check); a purchase modal opened meanwhile keeps its own resolver
    async function switchToPromoCode() {
        const promoCodeStore = usePromoCodeStore()
        const resolve = resolveModalFn.value
        resolveModalFn.value = null
        closeAdDisabledModal()
        // ModalContainer's scroll restore needs the leave transition to end before the next dialog opens
        await waitUntil(() => layoutStore.isPageCovered).toBe(false)
        await promoCodeStore.openModal('no-ads-modal')
        await waitUntil(() => promoCodeStore.isModalOpened).toBe(false)
        await promoCodeStore.getPending()
        checkAdDisabled()
        resolve?.(getAdFreeResult())
    }

    function closeNoAdFinalScreen() {
        isAdFinalScreenVisible.value = false
    }

    async function openDisabledAdModal(
        trigger: string,
        placement?: AdType,
    ): Promise<ChargeResponse<DisableAdOrder> | null> {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'open_purchase_window',
            adPlacement: placement,
        })

        try {
            const currentUser = await userStore.authorizeUser('disable-ad-modal')

            if (currentUser) {
                adDisabledModalOptions.value = {
                    isOpened: true,
                    isTopUp: isAdFreeActive.value,
                    placement,
                    trigger,
                }
                return new Promise<ChargeResponse<DisableAdOrder> | null>((resolve) => {
                    resolveModalFn.value = resolve
                })
            }
        } catch {}

        return null
    }

    async function disableAd(durationInDays: 1 | 7 | 30) {
        adDisabledModalOptions.value.isOpened = false
        const purchasePath = route.path

        try {
            const result = await chargeUserWithNativeExactPack({
                type: 'disable-ad-order',
                amount: pricesConfig.value.basic[durationInDays],
                durationInDays,
                attributes: {
                    clid: appStore.clid,
                    widgetId: clientAppStore.widgetId,
                    userAcquisitionParams: clientAppStore.userAcquisitionParams,
                },
                externalId: `no-ads-${durationInDays}-days-${Date.now()}`,
                placement: adDisabledModalOptions.value.placement,
                subscription: 'basic-days',
                isTopUp: adDisabledModalOptions.value.isTopUp,
            })
            resolveModalFn.value?.((result as ChargeResponse<DisableAdOrder>) ?? null)
            resolveModalFn.value = null
            isAdFinalScreenVisible.value = route.path === purchasePath
        } catch {
            resolveModalFn.value?.(null)
            resolveModalFn.value = null
        }

        adDisabledModalOptions.value.placement = undefined
    }

    return {
        isAdDisabled,
        isAdFreeActive,
        remainingMsWithoutAd,
        disableAd,
        adDisabledModalOptions,
        openDisabledAdModal,
        closeAdDisabledModal,
        switchToPromoCode,
        isAdFinalScreenVisible,
        closeNoAdFinalScreen,
        checkAdDisabled,
        pricesConfig,
    }
})
